(() => {
  const STACK_MEDIA_ID = 'camo-flavor-stack';
  const MAIN_LIST_SELECTOR = 'xo-carousel > * xo-carousel-inner > xo-carousel-list';
  const MOBILE_QUERY = '(max-width: 749px)';
  const SUPPORTED_LAYOUT_VERSION = 1;
  const LAYOUT_DEFAULTS = Object.freeze({
    desktop: Object.freeze({
      spread: 22,
      max_centered_layers: 15,
      scale_base: 1,
      scale_progression: 0.004,
      scale_min: 0.94,
      opacity_base: 1,
      opacity_progression: 0.035,
      opacity_min: 0.2,
      z_index_base: 40,
      z_index_progression: 1,
      stage_top: '13%',
      stage_bottom: '2%',
      transform_origin: '50% 54%',
      opacity_transition_duration: '220ms',
      opacity_transition_easing: 'ease',
      transform_transition_duration: '280ms',
      transform_transition_easing: 'cubic-bezier(.22, .61, .36, 1)',
      thumbnail_width: '38%',
      thumbnail_height: '72%',
      thumbnail_border_width: '1px',
      thumbnail_border_radius: '.35rem',
      thumbnail_layer_offset: '18%',
      thumbnail_layer_one_opacity: 0.55,
      thumbnail_layer_two_opacity: 0.75,
      thumbnail_layer_three_opacity: 1,
    }),
    mobile: Object.freeze({
      spread: 12,
      max_centered_layers: 15,
      scale_base: 1,
      scale_progression: 0.004,
      scale_min: 0.94,
      opacity_base: 1,
      opacity_progression: 0.035,
      opacity_min: 0.2,
      z_index_base: 40,
      z_index_progression: 1,
      stage_top: '15%',
      stage_bottom: '1%',
      transform_origin: '50% 54%',
      opacity_transition_duration: '220ms',
      opacity_transition_easing: 'ease',
      transform_transition_duration: '280ms',
      transform_transition_easing: 'cubic-bezier(.22, .61, .36, 1)',
      thumbnail_width: '38%',
      thumbnail_height: '72%',
      thumbnail_border_width: '1px',
      thumbnail_border_radius: '.35rem',
      thumbnail_layer_offset: '18%',
      thumbnail_layer_one_opacity: 0.55,
      thumbnail_layer_two_opacity: 0.75,
      thumbnail_layer_three_opacity: 1,
    }),
  });

  const NUMBER_RULES = Object.freeze({
    spread: [0, 500],
    max_centered_layers: [1, 100],
    scale_base: [0.01, 10],
    scale_progression: [0, 1],
    scale_min: [0.01, 10],
    opacity_base: [0, 1],
    opacity_progression: [0, 1],
    opacity_min: [0, 1],
    z_index_base: [-10000, 10000],
    z_index_progression: [-1000, 1000],
    thumbnail_layer_one_opacity: [0, 1],
    thumbnail_layer_two_opacity: [0, 1],
    thumbnail_layer_three_opacity: [0, 1],
  });
  const CSS_VALUE_KEYS = new Set([
    'stage_top', 'stage_bottom', 'transform_origin',
    'opacity_transition_duration', 'opacity_transition_easing',
    'transform_transition_duration', 'transform_transition_easing',
    'thumbnail_width', 'thumbnail_height', 'thumbnail_border_width',
    'thumbnail_border_radius', 'thumbnail_layer_offset',
  ]);

  const normalizeKey = (value) => String(value ?? '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '');
  const isPlainObject = (value) => Boolean(value) && typeof value === 'object' && !Array.isArray(value);
  const isUsableCssValue = (value) => typeof value === 'string'
    && value.trim() !== ''
    && !/[;{}]/.test(value)
    && !/^(undefined|null|nan)$/i.test(value.trim());

  const parseLayoutConfig = (source) => {
    const raw = source.getAttribute('data-camo-product-flavor-stack-layout');
    if (!raw || raw === 'null') return null;

    try {
      let parsed = JSON.parse(raw);
      if (typeof parsed === 'string') parsed = JSON.parse(parsed);
      if (!isPlainObject(parsed) || parsed.version !== SUPPORTED_LAYOUT_VERSION || !isPlainObject(parsed.layouts)) return null;
      return parsed;
    } catch (error) {
      return null;
    }
  };

  const selectedOptionValue = (source, product, optionName) => {
    const normalizedName = normalizeKey(optionName);
    const fields = Array.from(product.querySelectorAll('input[name], select[name]'));
    const matching = fields.filter((field) => {
      const fieldName = normalizeKey(field.name);
      const optionDataName = normalizeKey(field.dataset.optionName || field.getAttribute('xo-name') || '');
      return fieldName === normalizedName
        || fieldName.endsWith(normalizedName)
        || optionDataName === normalizedName;
    });
    const checked = matching.find((field) => field.matches('input:checked'));
    const select = matching.find((field) => field.matches('select'));
    const liveValue = String(checked?.value || select?.value || '').trim();
    if (liveValue) return liveValue;

    return optionName === 'Type'
      ? String(source.dataset.camoProductFlavorStackType || '').trim()
      : String(source.dataset.camoProductFlavorStackPackType || '').trim();
  };

  const sanitizeLayout = (candidate, device) => {
    const result = { ...LAYOUT_DEFAULTS[device] };
    if (!isPlainObject(candidate)) return result;
    Object.keys(result).forEach((key) => {
      const value = candidate[key];
      if (Object.prototype.hasOwnProperty.call(NUMBER_RULES, key)) {
        if (typeof value !== 'number' || !Number.isFinite(value)) return;
        const [minimum, maximum] = NUMBER_RULES[key];
        if (value < minimum || value > maximum) return;
        result[key] = key === 'max_centered_layers' ? Math.round(value) : value;
        return;
      }
      if (CSS_VALUE_KEYS.has(key) && isUsableCssValue(value)) result[key] = value.trim();
    });
    result.scale_min = Math.min(result.scale_min, result.scale_base);
    result.opacity_min = Math.min(result.opacity_min, result.opacity_base);
    return result;
  };

  const resolveLayout = (source, product, config, device) => {
    const typeKey = normalizeKey(selectedOptionValue(source, product, 'Type'));
    const packTypeKey = normalizeKey(selectedOptionValue(source, product, 'Pack Type'));
    const candidate = config?.layouts?.[typeKey]?.[packTypeKey]?.[device];
    return sanitizeLayout(candidate, device);
  };

  const applyLayoutVariables = (visual, layout) => {
    visual.style.setProperty('--camo-product-stack-spread', `${layout.spread}px`);
    visual.style.setProperty('--camo-product-stack-stage-top', layout.stage_top);
    visual.style.setProperty('--camo-product-stack-stage-bottom', layout.stage_bottom);
    visual.style.setProperty('--camo-product-stack-transform-origin', layout.transform_origin);
    visual.style.setProperty('--camo-product-stack-opacity-duration', layout.opacity_transition_duration);
    visual.style.setProperty('--camo-product-stack-opacity-easing', layout.opacity_transition_easing);
    visual.style.setProperty('--camo-product-stack-transform-duration', layout.transform_transition_duration);
    visual.style.setProperty('--camo-product-stack-transform-easing', layout.transform_transition_easing);
    visual.style.setProperty('--camo-product-stack-thumbnail-width', layout.thumbnail_width);
    visual.style.setProperty('--camo-product-stack-thumbnail-height', layout.thumbnail_height);
    visual.style.setProperty('--camo-product-stack-thumbnail-border-width', layout.thumbnail_border_width);
    visual.style.setProperty('--camo-product-stack-thumbnail-border-radius', layout.thumbnail_border_radius);
    visual.style.setProperty('--camo-product-stack-thumbnail-layer-offset', layout.thumbnail_layer_offset);
    visual.style.setProperty('--camo-product-stack-thumbnail-layer-one-opacity', String(layout.thumbnail_layer_one_opacity));
    visual.style.setProperty('--camo-product-stack-thumbnail-layer-two-opacity', String(layout.thumbnail_layer_two_opacity));
    visual.style.setProperty('--camo-product-stack-thumbnail-layer-three-opacity', String(layout.thumbnail_layer_three_opacity));
  };

  let carouselRefreshId = 0;

  const refreshCarousel = (carousel) => {
    if (!carousel) return;
    carouselRefreshId += 1;
    const observedValue = `camo-stack-${carouselRefreshId}`;

    carousel.setAttribute('xo-observed', observedValue);
    carousel.querySelectorAll('xo-carousel-list, xo-carousel-thumbnail').forEach((element) => {
      element.setAttribute('xo-observed', observedValue);
    });
  };

  const getPrimaryLists = (mediaRoot) => Array.from(mediaRoot.querySelectorAll('xo-carousel')).flatMap((carousel) => {
    const thumbnail = carousel.querySelector(':scope > xo-carousel-thumbnail');
    return Array.from(carousel.querySelectorAll('xo-carousel-list')).filter((list) => !thumbnail?.contains(list));
  });

  const getThumbnailLists = (mediaRoot) => Array.from(mediaRoot.querySelectorAll('xo-carousel-thumbnail xo-carousel-list'));

  const stackIsActive = (mediaRoot) => {
    const primarySlides = getPrimaryLists(mediaRoot).flatMap((list) => (
      Array.from(list.querySelectorAll(':scope > xo-carousel-slide:not([xo-cloned])'))
    ));
    const activePrimary = primarySlides.find((slide) => (
      slide.hasAttribute('xo-active') && slide.getAttribute('xo-active') !== 'false'
    ));
    if (activePrimary) return activePrimary.hasAttribute('data-camo-product-flavor-stack-slide');
    return mediaRoot.dataset.camoFlavorStackActive === 'true';
  };

  const makeSlide = (source, list, isThumbnail = false) => {
    const slide = document.createElement('xo-carousel-slide');
    slide.dataset.camoProductFlavorStackSlide = '';
    slide.dataset.mediaId = STACK_MEDIA_ID;

    const sample = list.querySelector('xo-carousel-slide:not([data-camo-product-flavor-stack-slide])');
    if (sample?.className) slide.className = sample.className;
    if (sample?.getAttribute('style')) slide.setAttribute('style', sample.getAttribute('style'));

    if (isThumbnail) {
      slide.dataset.camoProductFlavorStackThumbnail = '';
      slide.setAttribute('aria-label', 'Selected flavor stack');
      slide.innerHTML = `
        <div class="camo-product-flavor-stack__thumbnail" aria-hidden="true">
          <span class="camo-product-flavor-stack__thumbnail-layer"></span>
          <span class="camo-product-flavor-stack__thumbnail-layer"></span>
          <span class="camo-product-flavor-stack__thumbnail-layer"></span>
        </div>`;
      return slide;
    }

    const visual = document.createElement('div');
    visual.className = 'camo-product-flavor-stack camo-product-flavor-stack--gallery-slide';
    visual.dataset.camoProductFlavorStackVisual = '';
    visual.setAttribute('aria-hidden', 'true');
    visual.innerHTML = `
      <div class="camo-product-flavor-stack__title" data-camo-product-flavor-stack-title>
        <span class="camo-product-flavor-stack__title-default"></span>
        <span class="camo-product-flavor-stack__title-flavor" data-camo-product-flavor-stack-flavor-title></span>
      </div>
      <div class="camo-product-flavor-stack__stage" data-camo-product-flavor-stack-stage></div>`;

    visual.querySelector('.camo-product-flavor-stack__title-default').textContent = source.dataset.productTitle || '';
    slide.appendChild(visual);
    return slide;
  };

  const initStack = (source) => {
    if (!source || source.dataset.camoProductFlavorStackReady === 'true') return;
    source.dataset.camoProductFlavorStackReady = 'true';

    const product = source.closest('xo-product') || document;
    const mediaRoot = source.closest('.main-product__media-with-camo-stack') || product;
    const layoutConfig = parseLayoutConfig(source);
    const mobileQuery = window.matchMedia(MOBILE_QUERY);
    const flavorOrder = (() => {
      try {
        const values = JSON.parse(source.dataset.camoProductFlavorStackOrder || '[]');
        return new Map(Array.isArray(values)
          ? values.map((value, index) => [normalizeKey(value), index])
          : []);
      } catch (error) {
        return new Map();
      }
    })();
    const templates = new Map();
    const templatesBySelection = new Map();
    source.querySelectorAll('[data-camo-product-flavor-stack-template]').forEach((template) => {
      const id = String(template.dataset.variantId || '');
      if (id) templates.set(id, template);
      const selectionKey = [
        normalizeKey(template.dataset.stackType),
        normalizeKey(template.dataset.stackPackType),
        normalizeKey(template.dataset.flavorName),
      ].join('|');
      if (selectionKey !== '||') templatesBySelection.set(selectionKey, template);
    });

    const templateForFlavor = (flavor, fallbackId = '') => {
      const selectionKey = [
        normalizeKey(selectedOptionValue(source, product, 'Type')),
        normalizeKey(selectedOptionValue(source, product, 'Pack Type')),
        normalizeKey(flavor),
      ].join('|');
      return templatesBySelection.get(selectionKey) || templates.get(String(fallbackId || '')) || null;
    };

    let lastSignature = '';
    let lastTotal = 0;
    let stackNavigationId = 0;

    const ensureSlides = () => {
      const slides = [];
      getPrimaryLists(mediaRoot).forEach((list) => {
        const carousel = list.closest('xo-carousel');
        let slide = list.querySelector('[data-camo-product-flavor-stack-slide]');
        if (!slide) {
          slide = makeSlide(source, list, false);
          list.appendChild(slide);
          refreshCarousel(carousel);
          requestAnimationFrame(() => {
            const sample = list.querySelector('xo-carousel-slide:not([data-camo-product-flavor-stack-slide])');
            if (sample?.getAttribute('style')) slide.setAttribute('style', sample.getAttribute('style'));
          });
        }
        slides.push(slide);
      });

      getThumbnailLists(mediaRoot).forEach((list) => {
        if (!list.querySelector('[data-camo-product-flavor-stack-thumbnail]')) {
          const thumb = makeSlide(source, list, true);
          list.appendChild(thumb);
          refreshCarousel(list.closest('xo-carousel'));
        }
      });
      return slides;
    };

    const removeSlides = () => {
      mediaRoot.querySelectorAll('[data-camo-product-flavor-stack-slide], [data-camo-product-flavor-stack-thumbnail]').forEach((slide) => {
        const carousel = slide.closest('xo-carousel');
        slide.remove();
        refreshCarousel(carousel);
      });
    };

    const navigateToStack = () => {
      mediaRoot.dataset.camoFlavorStackActive = 'true';
      mediaRoot.querySelectorAll('xo-carousel').forEach((carousel) => {
        const list = getPrimaryLists(carousel)[0];
        const slide = list?.querySelector('[data-camo-product-flavor-stack-slide]');
        if (!list || !slide) return;

        const slides = Array.from(list.querySelectorAll(':scope > xo-carousel-slide:not([xo-cloned])'));
        const index = slides.indexOf(slide);
        if (index < 0) return;

        if (window.xoCarousel?.goTo && carousel.xoName) {
          window.xoCarousel.goTo(carousel.xoName, index);
          return;
        }

        const thumb = carousel.querySelector('[data-camo-product-flavor-stack-thumbnail]');
        if (thumb) thumb.click();
      });
    };

    const renderVisual = (visual, ordered, total, activeVariantId, layout) => {
      const stage = visual.querySelector('[data-camo-product-flavor-stack-stage]');
      const title = visual.querySelector('[data-camo-product-flavor-stack-flavor-title]');
      if (!stage) return;

      applyLayoutVariables(visual, layout);
      stage.querySelectorAll('[data-camo-product-flavor-stack-clone]').forEach((node) => node.remove());
      const spread = layout.spread;
      const centeredCount = Math.min(total, layout.max_centered_layers);
      const centerOffset = centeredCount > 1 ? ((centeredCount - 1) * spread) / 2 : 0;
      const fragment = document.createDocumentFragment();
      let index = 0;

      ordered.forEach((group) => {
        const template = templates.get(group.id);
        if (!template) return;
        for (let groupIndex = 0; groupIndex < group.qty; groupIndex += 1) {
          const clone = template.cloneNode(true);
          const visibleFromFront = Math.max(0, total - 1 - index);
          clone.classList.remove('camo-product-flavor-stack__card--template');
          clone.removeAttribute('data-camo-product-flavor-stack-template');
          clone.setAttribute('data-camo-product-flavor-stack-clone', '');
          clone.setAttribute('data-stack-group-id', group.id);
          clone.style.setProperty('--camo-stack-x', `${centerOffset - visibleFromFront * spread}px`);
          clone.style.setProperty('--camo-stack-scale', Math.max(layout.scale_min, layout.scale_base - visibleFromFront * layout.scale_progression).toFixed(3));
          clone.style.setProperty('--camo-stack-opacity', Math.max(layout.opacity_min, layout.opacity_base - visibleFromFront * layout.opacity_progression).toFixed(3));
          clone.style.setProperty('--camo-stack-z', String(layout.z_index_base + index * layout.z_index_progression));
          fragment.appendChild(clone);
          index += 1;
        }
      });

      stage.appendChild(fragment);
      visual.classList.toggle('has-camo-flavor-picks', total > 0);
      const activeGroup = ordered.find((group) => group.id === activeVariantId) || ordered[ordered.length - 1];
      if (title) title.textContent = String(activeGroup?.item?.flavor || '').toUpperCase();
    };

    const render = (detail = {}, options = {}) => {
      const items = Array.isArray(detail.items) ? detail.items : [];
      const activeVariantId = String(detail.activeVariantId || '');
      const groups = items
        .map((item) => {
          const template = templateForFlavor(item?.flavor, item?.variantId);
          return {
            item,
            id: String(template?.dataset.variantId || item?.variantId || ''),
            qty: Math.max(0, Number(item?.qty || 0)),
          };
        })
        .filter((group) => group.id && group.qty > 0 && templates.has(group.id));
      const shopifyOrderedGroups = groups
        .map((group, index) => ({ group, index }))
        .sort((left, right) => {
          const leftOrder = flavorOrder.get(normalizeKey(left.group.item?.flavor));
          const rightOrder = flavorOrder.get(normalizeKey(right.group.item?.flavor));
          const leftRank = leftOrder === undefined ? Number.MAX_SAFE_INTEGER : leftOrder;
          const rightRank = rightOrder === undefined ? Number.MAX_SAFE_INTEGER : rightOrder;
          return leftRank - rightRank || left.index - right.index;
        })
        .map(({ group }) => group);
      const ordered = shopifyOrderedGroups.filter((group) => group.id !== activeVariantId)
        .concat(shopifyOrderedGroups.filter((group) => group.id === activeVariantId));
      const total = ordered.reduce((sum, group) => sum + group.qty, 0);
      const activeFlavor = items.find((item) => String(item?.variantId || '') === activeVariantId)?.flavor
        || ordered.find((group) => group.item?.flavor)?.item?.flavor
        || '';
      const activeTemplate = templateForFlavor(activeFlavor, activeVariantId);
      const resolvedActiveVariantId = String(activeTemplate?.dataset.variantId || activeVariantId || '');
      const signature = JSON.stringify({ activeVariantId: resolvedActiveVariantId, items: ordered.map((group) => [group.id, group.qty]) });
      const preserveStack = stackIsActive(mediaRoot);
      const firstStackSelection = lastTotal === 0 && total > 0;
      const focusStack = options.focusStack === true;

      if (!total) {
        removeSlides();
        lastSignature = signature;
        lastTotal = 0;
        mediaRoot.dataset.camoFlavorStackActive = 'false';
        return;
      }

      const device = mobileQuery.matches ? 'mobile' : 'desktop';
      const layout = resolveLayout(source, product, layoutConfig, device);
      const slides = ensureSlides();
      slides.forEach((slide) => {
        const visual = slide.querySelector('[data-camo-product-flavor-stack-visual]');
        if (visual) renderVisual(visual, ordered, total, resolvedActiveVariantId, layout);
      });
      mediaRoot.querySelectorAll('.camo-product-flavor-stack__thumbnail').forEach((thumbnail) => {
        applyLayoutVariables(thumbnail, layout);
      });

      if ((focusStack || firstStackSelection || preserveStack) && signature !== lastSignature) {
        window.clearTimeout(stackNavigationId);
        stackNavigationId = window.setTimeout(() => {
          requestAnimationFrame(() => requestAnimationFrame(navigateToStack));
        }, 0);
      }
      lastSignature = signature;
      lastTotal = total;
    };

    let lastDetail = {};
    product.addEventListener('camo:product-flavor-visual-state', (event) => {
      lastDetail = event.detail || {};
      render(lastDetail, { focusStack: event.detail?.focusStack === true });
    });
    product.addEventListener('change', (event) => {
      if (event.target?.closest?.('[data-camo-flavor-view-toggle]')) return;
      const name = normalizeKey(event.target?.name || event.target?.dataset?.optionName || '');
      if (name && !name.endsWith('type') && !name.endsWith('packtype') && name !== 'id') return;
      render(lastDetail);
    });
    mobileQuery.addEventListener?.('change', () => render(lastDetail));
  };

  const init = (scope = document) => scope.querySelectorAll('[data-camo-product-flavor-stack-source]').forEach(initStack);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
